import { fetchFastApi } from "./fastapi";
import {
  presignedUrlResponseSchema,
  PresignedUrlItem,
} from "../models/presignedUpload";

export const PRESIGNED_UPLOAD_THRESHOLD_BYTES = 50 * 1024 * 1024; // 50MB

/**
 * Check if any file exceeds the presigned upload threshold
 */
export function shouldUsePresignedUpload(files: File[]): boolean {
  return files.some((file) => file.size > PRESIGNED_UPLOAD_THRESHOLD_BYTES);
}

/**
 * Request presigned URLs for uploading files directly to S3
 */
export async function requestPresignedUrls(
  files: { filename: string; contentType: string }[],
): Promise<PresignedUrlItem[]> {
  const response = await fetchFastApi({
    path: "/uploads/request-upload-urls",
    method: "POST",
    body: {
      files: files.map((f) => ({
        filename: f.filename,
        content_type: f.contentType,
      })),
    },
  });

  const parsed = presignedUrlResponseSchema.parse(response);
  return parsed.uploads;
}

/**
 * Upload a file directly to S3 using a presigned URL
 * Uses XMLHttpRequest for progress tracking
 */
export function uploadFileToS3(
  file: File,
  presignedUrl: string,
  contentType: string,
  requiresServerSideEncryption: boolean,
  onProgress?: (progress: number) => void,
  abortSignal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Handle abort signal
    // A signal already aborted before we got here fires no event, so the
    // listener below would never run and the whole archive would upload with
    // nobody waiting for it. Reachable by cancelling while the presigned-URL
    // request is still in flight.
    if (abortSignal?.aborted) {
      reject(new DOMException("Upload aborted", "AbortError"));
      return;
    }
    if (abortSignal) {
      abortSignal.addEventListener("abort", () => {
        xhr.abort();
        reject(new DOMException("Upload aborted", "AbortError"));
      });
    }

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener("error", () => {
      reject(new Error("Network error during S3 upload"));
    });

    xhr.addEventListener("abort", () => {
      reject(new DOMException("Upload aborted", "AbortError"));
    });

    xhr.open("PUT", presignedUrl);
    // Content-Type must always match what was signed in the presigned URL.
    // The SSE header is conditional: S3 rejects a presigned PUT that sends
    // an x-amz-* header not present in what was signed (AccessDenied:
    // "headers present in the request which were not signed" - verified
    // empirically), so this header must be sent if and only if the backend
    // says it signed ServerSideEncryption for this specific URL.
    xhr.setRequestHeader("Content-Type", contentType);
    if (requiresServerSideEncryption) {
      xhr.setRequestHeader("x-amz-server-side-encryption", "AES256");
    }
    xhr.send(file);
  });
}
